import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { DataTable } from "@/components/ui/data-table";
import {
  PlusCircle,
  Package,
  Edit,
  Trash2,
  Search,
  MoreVertical,
} from "lucide-react";
import { API_ENDPOINTS } from "../../api/header";
import apiClient from "../../api/apiClient";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/spinner";
import Modal from "@/components/ui/modal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ReactSelect } from "@/components/ui/react-select";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { PageContainer } from "@/components/layout/PageContainer";
import RecordNotFound from "@/core/components/RecordNotFound";

export const Units = () => {
  const queryClient = useQueryClient();
  const [unitData, setUnitData] = useState({
    id: null,
    name: "",
    short_name: "",
    description: "",
    is_active: true,
    is_default: true,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    description: "",
    onConfirm: null,
  });

  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  // Queries
  const { data: unitsData, isLoading } = useQuery({
    queryKey: ["units", currentPage, pageSize, searchText],
    queryFn: async () => {
      const response = await apiClient.get(API_ENDPOINTS.INVENTORY.UNITS, {
        params: {
          page: currentPage,
          per_page: pageSize,
          search: searchText || undefined,
        },
      });
      return response.data;
    },
  });

  const units = unitsData?.data || [];
  const paginationMeta = {
    current_page: unitsData?.meta?.current_page || unitsData?.current_page || 1,
    last_page: unitsData?.meta?.last_page || unitsData?.last_page || 1,
    per_page: unitsData?.meta?.per_page || unitsData?.per_page || pageSize,
    total: unitsData?.meta?.total || unitsData?.total || units.length,
  };

  // Mutations
  const saveUnitMutation = useMutation({
    mutationFn: async (apiData) => {
      const id = apiData.id?.toString();
      const url = id
        ? API_ENDPOINTS.INVENTORY.UNIT_DETAILS(id)
        : API_ENDPOINTS.INVENTORY.UNITS;
      const method = id ? "PUT" : "POST";
      return await apiClient({ method, url, data: apiData });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["units"] });
      toast.success("Unit saved successfully!");
      setModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Something went wrong!");
    },
  });

  const deleteUnitMutation = useMutation({
    mutationFn: async (id) => {
      return await apiClient.delete(API_ENDPOINTS.INVENTORY.UNIT_DETAILS(id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["units"] });
      toast.success("Unit deleted successfully!");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to delete unit");
    },
  });

  // Reset page when search changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCurrentPage(1);
  }, [searchText]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUnitData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddUnit = () => {
    setUnitData({
      id: null,
      name: "",
      short_name: "",
      description: "",
      is_active: true,
      is_default: true,
    });
    setEditMode(false);
    setModalOpen(true);
  };

  const handleEditUnit = (unit) => {
    // Ensure boolean values are booleans
    setUnitData({
      ...unit,
      is_active: Boolean(unit.is_active),
      is_default: Boolean(unit.is_default),
    });
    setEditMode(true);
    setModalOpen(true);
  };

  const handleDelete = (unit) => {
    setConfirmDialog({
      isOpen: true,
      title: "Are you sure?",
      description: "You won't be able to revert this!",
      onConfirm: () => deleteUnitMutation.mutate(unit.id),
    });
  };

  const handleSubmit = async () => {
    saveUnitMutation.mutate(unitData);
  };

  const columns = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "name",
      header: "Unit Name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      accessorKey: "short_name",
      header: "Short Name",
      sorter: (a, b) => a.short_name.localeCompare(b.short_name),
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ getValue }) => getValue() || "-",
    },
    {
      accessorKey: "is_default",
      header: "Default",
      cell: ({ getValue }) => (getValue() ? "Yes" : "No"),
    },
    {
      accessorKey: "is_active",
      header: "Status",
      cell: ({ getValue }) => (
        <Badge variant={getValue() ? "success" : "destructive"}>
          {getValue() ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {hasPermission("unit update") && (
              <DropdownMenuItem onClick={() => handleEditUnit(row.original)}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
            )}
            {hasPermission("unit delete") && (
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => handleDelete(row.original)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  if (!hasPermission("unit read")) {
    return <RecordNotFound />;
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading title="Units" subtitle="Manage your units" />
        <PageHeaderActions>
          {hasPermission("unit create") && (
            <Button onClick={handleAddUnit}>
              <PlusCircle className="mr-2 h-4 w-4" /> Add New Unit
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <FilterCard>
        <FilterSearch
          placeholder="Search units..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={units}
          isLoading={isLoading}
          enableRowSelection={true}
          pageCount={paginationMeta.last_page}
          footerInfo={
            <span className="font-medium text-foreground">
              Total Units: {paginationMeta.total || 0}
            </span>
          }
          pagination={{
            pageIndex: currentPage - 1,
            pageSize: pageSize,
          }}
          onPaginationChange={(updater) => {
            const newState =
              typeof updater === "function"
                ? updater({
                    pageIndex: currentPage - 1,
                    pageSize: pageSize,
                  })
                : updater;
            setCurrentPage(newState.pageIndex + 1);
            setPageSize(newState.pageSize);
          }}
        />
      </TableCard>

      <Modal
        open={modalOpen}
        onOpenChange={setModalOpen}
        title={editMode ? "Edit Unit" : "Add New Unit"}
        footer={
          <div className="flex justify-end gap-3 -mx-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saveUnitMutation.isPending}
            >
              {saveUnitMutation.isPending ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        }
      >
        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <Label>Unit Name</Label>
            <Input
              name="name"
              placeholder="Enter unit name"
              value={unitData.name}
              onChange={handleInputChange}
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label>Short Name</Label>
            <Input
              name="short_name"
              placeholder="Enter short name"
              value={unitData.short_name}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              name="description"
              placeholder="Enter description"
              value={unitData.description}
              onChange={handleInputChange}
              rows={3}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Active</Label>
              <ReactSelect
                options={[
                  { value: "true", label: "Yes" },
                  { value: "false", label: "No" },
                ]}
                value={
                  unitData.is_active
                    ? { value: "true", label: "Yes" }
                    : { value: "false", label: "No" }
                }
                onChange={(opt) =>
                  setUnitData({
                    ...unitData,
                    is_active: opt?.value === "true",
                  })
                }
                isSearchable={false}
              />
            </div>
            <div className="space-y-2">
              <Label>Default</Label>
              <ReactSelect
                options={[
                  { value: "true", label: "Yes" },
                  { value: "false", label: "No" },
                ]}
                value={
                  unitData.is_default
                    ? { value: "true", label: "Yes" }
                    : { value: "false", label: "No" }
                }
                onChange={(opt) =>
                  setUnitData({
                    ...unitData,
                    is_default: opt?.value === "true",
                  })
                }
                isSearchable={false}
              />
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={confirmDialog.isOpen}
        onOpenChange={(open) =>
          setConfirmDialog((prev) => ({ ...prev, isOpen: open }))
        }
        title={confirmDialog.title}
        footer={
          <div className="flex justify-end gap-3 -mx-2">
            <Button
              variant="outline"
              onClick={() =>
                setConfirmDialog((prev) => ({ ...prev, isOpen: false }))
              }
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                confirmDialog.onConfirm?.();
                setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
              }}
            >
              Delete
            </Button>
          </div>
        }
      >
        <div className="py-4">
          <p className="text-sm text-muted-foreground font-medium">
            {confirmDialog.description}
          </p>
        </div>
      </Modal>
    </PageContainer>
  );
};

